import React, { useState, useRef, useEffect } from 'react';

/**
 * Modal Avanzado para Imagen Fantasma (Ghost Image / LetterScreen)
 * Convierte imágenes en patrones de texto para seguridad avanzada
 */
const GhostImageModal = ({ isOpen, onClose, onGenerate }) => {
    // Estados de archivo
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Configuración básica
    const [fontSize, setFontSize] = useState(12);
    const [density, setDensity] = useState(55);
    const [resolution, setResolution] = useState(360);
    const [gamma, setGamma] = useState(1.0);

    // Configuración de caracteres
    const [charType, setCharType] = useState('ramp');
    const [rampChars, setRampChars] = useState('@#%*+=-:. ');
    const [customChars, setCustomChars] = useState('');

    // Configuración de algoritmo
    const [algorithm, setAlgorithm] = useState('standard'); // standard, edge, dual, ascii
    const [smoothing, setSmoothing] = useState('none'); // none, light, strong
    const [invertColors, setInvertColors] = useState(false);

    // Configuración de estilo
    const [textColor, setTextColor] = useState('#111827');
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');
    const [bold, setBold] = useState(false);
    const [jitter, setJitter] = useState(false);
    const [jitterAmount, setJitterAmount] = useState(2);

    // Procesamiento de imagen
    const [removeBackground, setRemoveBackground] = useState(true);
    const [cropToSubject, setCropToSubject] = useState(true);
    const [edgeBlur, setEdgeBlur] = useState(3);
    const [maskThreshold, setMaskThreshold] = useState(70);
    const [edgeTrim, setEdgeTrim] = useState(1);

    // Características avanzadas
    const [enableShadow, setEnableShadow] = useState(false);
    const [shadowColor, setShadowColor] = useState('#000000');
    const [shadowBlur, setShadowBlur] = useState(2);
    const [enableGradient, setEnableGradient] = useState(false);
    const [gradientColor1, setGradientColor1] = useState('#000000');
    const [gradientColor2, setGradientColor2] = useState('#666666');
    const [gradientAngle, setGradientAngle] = useState(45);

    // Características de seguridad
    const [enableWatermark, setEnableWatermark] = useState(false);
    const [watermarkText, setWatermarkText] = useState('ORIGINAL');
    const [enableMicrotext, setEnableMicrotext] = useState(false);
    const [microtextContent, setMicrotextContent] = useState('SECURITY');
    const [enableSerialNumber, setEnableSerialNumber] = useState(false);
    const [serialPrefix, setSerialPrefix] = useState('GH');

    // Optimización
    const [enableAntiAlias, setEnableAntiAlias] = useState(true);
    const [enableDithering, setEnableDithering] = useState(false);
    const [ditheringType, setDitheringType] = useState('floyd-steinberg'); // floyd-steinberg, atkinson, ordered

    const fileInputRef = useRef(null);
    const previewCanvasRef = useRef(null);

    useEffect(() => {
        if (selectedFile) {
            const url = URL.createObjectURL(selectedFile);
            setPreviewUrl(url);

            return () => URL.revokeObjectURL(url);
        }
    }, [selectedFile]);

    useEffect(() => {
        if (previewUrl && previewCanvasRef.current) {
            updatePreview();
        }
    }, [
        previewUrl,
        fontSize,
        density,
        charType,
        rampChars,
        customChars,
        algorithm,
        textColor,
        backgroundColor,
        bold,
        invertColors,
        gamma,
        jitter,
        jitterAmount,
        enableGradient,
        gradientColor1,
        gradientColor2,
        gradientAngle,
        enableShadow,
        shadowColor,
        shadowBlur
    ]);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
        }
    };

    // Función auxiliar para convertir hex a RGB
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
    };

    const updatePreview = () => {
        const canvas = previewCanvasRef.current;
        // Usar selectedFile directamente si está disponible, como en handleGenerate
        if (!canvas || (!previewUrl && !selectedFile)) {
            console.log('Preview: Canvas o Archivo no disponible');
            return;
        }

        const ctx = canvas.getContext('2d');
        const img = new Image();

        // Crear URL fresca si es posible, para evitar problemas con blobs revocados
        const srcToUse = selectedFile ? URL.createObjectURL(selectedFile) : previewUrl;

        img.onerror = (error) => {
            console.error('Error cargando imagen para preview:', error);
            if (selectedFile) URL.revokeObjectURL(srcToUse);
        };

        img.onload = () => {
            try {
                console.log('Preview: Imagen cargada', { width: img.width, height: img.height, src: srcToUse });

                // Calcular dimensiones manteniendo aspect ratio
                const maxWidth = 400;
                const maxHeight = 300;
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }

                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }

                // IMPORTANTE: Asegurar que las dimensiones sean enteros para evitar índices decimales
                width = Math.floor(width);
                height = Math.floor(height);

                canvas.width = width;
                canvas.height = height;

                // Dibujar imagen original para obtener datos
                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                const imageData = ctx.getImageData(0, 0, width, height);

                // SANITY CHECK: Verificar si hay píxeles no transparentes
                let maxAlpha = 0;
                let nonZeroPixels = 0;
                for (let i = 3; i < imageData.data.length; i += 4) {
                    if (imageData.data[i] > maxAlpha) maxAlpha = imageData.data[i];
                    if (imageData.data[i] > 10) nonZeroPixels++;
                }
                console.log('Preview: Análisis de datos de imagen', {
                    maxAlpha,
                    nonZeroPixels,
                    totalPixels: width * height,
                    ratio: nonZeroPixels / (width * height),
                    imageDataWidth: imageData.width
                });

                // Limpiar canvas con fondo
                ctx.fillStyle = backgroundColor;
                ctx.fillRect(0, 0, width, height);

                console.log('Preview: Colores', {
                    backgroundColor,
                    textColor,
                    areEqual: backgroundColor === textColor
                });

                // Configurar fuente
                const scaledFontSize = Math.max(6, parseInt(fontSize) * 0.8);
                ctx.font = `${bold ? '700' : '400'} ${scaledFontSize}px "Courier New", monospace`;
                ctx.textBaseline = 'top';

                console.log('Preview: Fuente configurada', {
                    font: ctx.font,
                    fontSize: scaledFontSize,
                    bold
                });

                // Obtener array de caracteres
                const chars = getCharArray();
                console.log('Preview: Caracteres', { count: chars.length, sample: chars.slice(0, 5).join('') });

                // Calcular grid basado en el tamaño de fuente
                const charWidth = scaledFontSize * 0.6;
                const charHeight = scaledFontSize;
                const cols = Math.floor(width / charWidth);
                const rows = Math.floor(height / charHeight);

                console.log('Preview: Grid', { cols, rows, charWidth, charHeight });

                // Dibujar caracteres
                let drawnChars = 0;
                let totalCells = 0;
                let cellsWithContent = 0;
                let cellsSkipped = 0;

                for (let row = 0; row < rows; row++) {
                    for (let col = 0; col < cols; col++) {
                        totalCells++;

                        // Calcular región de la imagen para esta celda
                        const startX = Math.floor((col * width) / cols);
                        const startY = Math.floor((row * height) / rows);
                        const endX = Math.floor(((col + 1) * width) / cols);
                        const endY = Math.floor(((row + 1) * height) / rows);

                        // Calcular brillo promedio de la celda
                        let brightness = 0;
                        let count = 0;
                        let hasContent = false;

                        for (let y = startY; y < endY && y < height; y++) {
                            for (let x = startX; x < endX && x < width; x++) {
                                // USAR imageData.width PARA EL ÍNDICE CORRECTO
                                const idx = (y * imageData.width + x) * 4;
                                const r = imageData.data[idx];
                                const g = imageData.data[idx + 1];
                                const b = imageData.data[idx + 2];
                                const alpha = imageData.data[idx + 3];

                                // Solo contar píxeles no transparentes (umbral reducido de 25 a 10)
                                if (alpha > 10) {
                                    brightness += (r + g + b) / 3;
                                    count++;
                                    hasContent = true;
                                }
                            }
                        }

                        // Si la celda está completamente transparente, saltar
                        if (!hasContent || count === 0) {
                            cellsSkipped++;
                            continue;
                        }

                        cellsWithContent++;

                        // Normalizar brillo a 0-1
                        brightness = brightness / count / 255;

                        // Aplicar gamma
                        brightness = Math.pow(brightness, parseFloat(gamma));

                        // Invertir si es necesario
                        if (invertColors) {
                            brightness = 1 - brightness;
                        }

                        // Seleccionar carácter basado en brillo
                        const charIndex = Math.floor(brightness * (chars.length - 1));
                        const char = chars[charIndex];

                        // Calcular posición de dibujo
                        let drawX = col * charWidth;
                        let drawY = row * charHeight;

                        // Aplicar jitter si está habilitado
                        if (jitter) {
                            drawX += (Math.random() - 0.5) * parseInt(jitterAmount);
                            drawY += (Math.random() - 0.5) * parseInt(jitterAmount);
                        }

                        // Aplicar color
                        if (enableGradient) {
                            const gradAngle = parseInt(gradientAngle) * Math.PI / 180;
                            const t = (drawX * Math.cos(gradAngle) + drawY * Math.sin(gradAngle)) /
                                Math.max(width, height);
                            const normalizedT = (t + 1) / 2;

                            const c1 = hexToRgb(gradientColor1);
                            const c2 = hexToRgb(gradientColor2);
                            const r = Math.round(c1.r + (c2.r - c1.r) * normalizedT);
                            const g = Math.round(c1.g + (c2.g - c1.g) * normalizedT);
                            const b = Math.round(c1.b + (c2.b - c1.b) * normalizedT);
                            ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
                        } else {
                            ctx.fillStyle = textColor;
                        }

                        // Aplicar sombra si está habilitada
                        if (enableShadow) {
                            ctx.shadowColor = shadowColor;
                            ctx.shadowBlur = parseInt(shadowBlur);
                            ctx.shadowOffsetX = 1;
                            ctx.shadowOffsetY = 1;
                        }

                        // Dibujar carácter con opacidad basada en brillo
                        ctx.globalAlpha = 0.3 + brightness * 0.7;
                        ctx.fillText(char, drawX, drawY);
                        drawnChars++;

                        // Log del primer carácter para debug
                        if (drawnChars === 1) {
                            console.log('Preview: Primer carácter dibujado', {
                                char,
                                charCode: char.charCodeAt(0),
                                brightness,
                                drawX,
                                drawY,
                                fillStyle: ctx.fillStyle,
                                globalAlpha: ctx.globalAlpha,
                                font: ctx.font
                            });
                        }
                    }
                }

                // Resetear efectos
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.globalAlpha = 1.0;

                console.log('Preview: Completado', {
                    drawnChars,
                    totalCells,
                    cellsWithContent,
                    cellsSkipped,
                    canvasWidth: canvas.width,
                    canvasHeight: canvas.height,
                    canvasStyle: canvas.style.cssText,
                    canvasDisplay: window.getComputedStyle(canvas).display,
                    canvasVisible: canvas.offsetWidth > 0 && canvas.offsetHeight > 0
                });
            } catch (error) {
                console.error('Error en updatePreview:', error);
            } finally {
                // Revocar URL solo si fue creada localmente y ya terminamos
                if (selectedFile && srcToUse) {
                    // Pequeño timeout para asegurar que la imagen no se necesite más
                    setTimeout(() => URL.revokeObjectURL(srcToUse), 100);
                }
            }
        };

        img.src = srcToUse;
        console.log('Preview: Iniciando carga de imagen', { src: srcToUse });
    };

    const getCharArray = () => {
        switch (charType) {
            case 'ramp':
                return rampChars.split('');
            case 'lower':
                return 'abcdefghijklmnopqrstuvwxyz'.split('');
            case 'upper':
                return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
            case 'alnum':
                return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('');
            case 'symbols':
                return '!@#$%^&*()_+-=[]{};<>?/|~'.split('');
            case 'custom':
                return customChars.split('') || ['.'];
            default:
                return '@#%*+=-:. '.split('');
        }
    };

    const generateSerialNumber = () => {
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `${serialPrefix}-${timestamp}-${random}`;
    };

    const handleGenerate = async () => {
        if (!selectedFile) {
            alert('Por favor selecciona una imagen');
            return;
        }

        try {
            // Cargar la imagen
            const img = new Image();
            const imgURL = URL.createObjectURL(selectedFile);

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = imgURL;
            });

            // Crear canvas temporal para procesar la imagen
            const targetWidth = parseInt(resolution);
            const aspect = img.height / img.width;
            const targetHeight = Math.round(aspect * targetWidth);

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = targetWidth;
            tempCanvas.height = targetHeight;
            const tempCtx = tempCanvas.getContext('2d');

            // Dibujar imagen original
            tempCtx.drawImage(img, 0, 0, targetWidth, targetHeight);
            const imageData = tempCtx.getImageData(0, 0, targetWidth, targetHeight);

            // Crear canvas para el resultado LetterScreen
            const resultCanvas = document.createElement('canvas');
            const scaledFontSize = parseInt(fontSize);
            const cols = Math.floor(targetWidth / (scaledFontSize * 0.6));
            const rows = Math.floor(targetHeight / scaledFontSize);

            resultCanvas.width = cols * scaledFontSize * 0.6;
            resultCanvas.height = rows * scaledFontSize;
            const ctx = resultCanvas.getContext('2d');

            // Fondo
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, resultCanvas.width, resultCanvas.height);

            // Configurar fuente
            ctx.font = `${bold ? '700' : '400'} ${scaledFontSize}px "Share Tech Mono", monospace`;
            ctx.textBaseline = 'top';

            // Obtener array de caracteres
            const chars = getCharArray();
            const cellWidth = targetWidth / cols;
            const cellHeight = targetHeight / rows;

            // Generar el efecto LetterScreen
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    // Calcular región de la imagen para esta celda
                    const startX = Math.floor((col * targetWidth) / cols);
                    const startY = Math.floor((row * targetHeight) / rows);
                    const endX = Math.floor(((col + 1) * targetWidth) / cols);
                    const endY = Math.floor(((row + 1) * targetHeight) / rows);

                    // Calcular brillo promedio de la celda
                    let brightness = 0;
                    let count = 0;

                    for (let y = startY; y < endY && y < targetHeight; y++) {
                        for (let x = startX; x < endX && x < targetWidth; x++) {
                            const idx = (y * targetWidth + x) * 4;
                            const r = imageData.data[idx];
                            const g = imageData.data[idx + 1];
                            const b = imageData.data[idx + 2];
                            const alpha = imageData.data[idx + 3];

                            // Solo contar píxeles no transparentes
                            if (alpha > 25) {
                                brightness += (r + g + b) / 3;
                                count++;
                            }
                        }
                    }

                    if (count === 0) continue; // Celda completamente transparente

                    brightness = brightness / count / 255;

                    // Aplicar gamma
                    brightness = Math.pow(brightness, parseFloat(gamma));

                    if (invertColors) {
                        brightness = 1 - brightness;
                    }

                    // Seleccionar carácter basado en brillo
                    // Caracteres oscuros (@#) para áreas oscuras, claros (. ) para áreas claras
                    const charIndex = Math.floor(brightness * (chars.length - 1));
                    const char = chars[charIndex];

                    // Calcular posición de dibujo
                    let drawX = col * scaledFontSize * 0.6;
                    let drawY = row * scaledFontSize;

                    // Aplicar jitter si está habilitado
                    if (jitter) {
                        drawX += (Math.random() - 0.5) * parseInt(jitterAmount);
                        drawY += (Math.random() - 0.5) * parseInt(jitterAmount);
                    }

                    // Aplicar efectos de color
                    if (enableGradient) {
                        const gradAngle = parseInt(gradientAngle) * Math.PI / 180;
                        const t = (drawX * Math.cos(gradAngle) + drawY * Math.sin(gradAngle)) /
                            Math.max(resultCanvas.width, resultCanvas.height);
                        const normalizedT = (t + 1) / 2; // Normalizar a 0-1

                        // Interpolar colores
                        const c1 = hexToRgb(gradientColor1);
                        const c2 = hexToRgb(gradientColor2);
                        const r = Math.round(c1.r + (c2.r - c1.r) * normalizedT);
                        const g = Math.round(c1.g + (c2.g - c1.g) * normalizedT);
                        const b = Math.round(c1.b + (c2.b - c1.b) * normalizedT);
                        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
                    } else {
                        ctx.fillStyle = textColor;
                    }

                    // Aplicar sombra si está habilitada
                    if (enableShadow) {
                        ctx.shadowColor = shadowColor;
                        ctx.shadowBlur = parseInt(shadowBlur);
                        ctx.shadowOffsetX = 1;
                        ctx.shadowOffsetY = 1;
                    }

                    // Dibujar carácter
                    ctx.globalAlpha = 0.3 + brightness * 0.7;
                    ctx.fillText(char, drawX, drawY);
                }
            }

            // Resetear efectos
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1.0;

            // Limpiar URL temporal
            URL.revokeObjectURL(imgURL);

            // Convertir a imagen
            const dataURL = resultCanvas.toDataURL('image/png');

            // Crear configuración
            const config = {
                src: dataURL,
                width: resultCanvas.width,
                height: resultCanvas.height,
                fontSize: parseInt(fontSize),
                density: parseInt(density),
                resolution: parseInt(resolution),
                gamma: parseFloat(gamma),
                charType,
                algorithm,
                smoothing,
                invertColors,
                textColor,
                backgroundColor,
                bold,
                jitter,
                jitterAmount: parseInt(jitterAmount),
                effects: {
                    shadow: enableShadow,
                    shadowColor,
                    shadowBlur: parseInt(shadowBlur),
                    gradient: enableGradient,
                    gradientColor1,
                    gradientColor2,
                    gradientAngle: parseInt(gradientAngle)
                },
                security: {
                    watermark: enableWatermark,
                    watermarkText,
                    microtext: enableMicrotext,
                    microtextContent,
                    serialNumber: enableSerialNumber ? generateSerialNumber() : null
                },
                customType: 'ghostImage',
                isSecurityFeature: true,
                securityLevel: 'advanced',
                metadata: {
                    createdAt: new Date().toISOString(),
                    originalFileName: selectedFile.name,
                    fileSize: selectedFile.size,
                    fileType: selectedFile.type
                }
            };

            onGenerate(config);
            onClose();
        } catch (error) {
            console.error('Error generando imagen fantasma:', error);
            alert('Error al procesar la imagen. Por favor intenta con otra imagen.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl w-full max-w-7xl overflow-hidden border border-slate-700">
                {/* Header */}
                <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-slate-900/50 to-gray-900/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-lg bg-gray-500/20 flex items-center justify-center">
                                <span className="text-3xl">👻</span>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white">Imagen Fantasma - LetterScreen Profesional</h3>
                                <p className="text-sm text-gray-300">Conversión avanzada de imagen a texto • Tecnología anti-falsificación</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[75vh] overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* Columna 1 - Imagen y Preview */}
                        <div className="lg:col-span-2 space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-blue-400 mb-3 flex items-center">
                                    <span className="mr-2">📁</span> Imagen Fuente
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full px-4 py-3 bg-slate-900 border-2 border-dashed border-slate-600 rounded-lg hover:border-blue-500 hover:bg-slate-800 transition-all text-slate-300 hover:text-white"
                                        >
                                            {selectedFile ? (
                                                <div className="flex items-center justify-center space-x-2">
                                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span>{selectedFile.name}</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center space-x-2">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                    </svg>
                                                    <span>Seleccionar Imagen</span>
                                                </div>
                                            )}
                                        </button>
                                    </div>

                                    {selectedFile && (
                                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                                            <div>
                                                <span className="font-medium">Tamaño:</span> {(selectedFile.size / 1024).toFixed(2)} KB
                                            </div>
                                            <div>
                                                <span className="font-medium">Tipo:</span> {selectedFile.type.split('/')[1].toUpperCase()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-green-400 mb-3 flex items-center">
                                    <span className="mr-2">👁️</span> Vista Previa LetterScreen
                                </h4>
                                <div className="w-full min-h-[300px] bg-white rounded-lg overflow-hidden border border-slate-600 flex items-center justify-center">
                                    {previewUrl ? (
                                        <canvas
                                            ref={previewCanvasRef}
                                            className="max-w-full max-h-full"
                                            style={{ display: 'block', border: '2px solid red' }}
                                        />
                                    ) : (
                                        <div className="text-center text-slate-400 p-8">
                                            <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <p>Selecciona una imagen para ver el preview</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Procesamiento de Imagen */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center">
                                    <span className="mr-2">🎨</span> Procesamiento de Imagen
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={removeBackground}
                                            onChange={(e) => setRemoveBackground(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">🤖 Remover fondo (IA)</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={cropToSubject}
                                            onChange={(e) => setCropToSubject(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">✂️ Recortar al sujeto</span>
                                    </label>

                                    {removeBackground && (
                                        <div className="ml-6 space-y-2 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">
                                                    Suavizado de borde: {edgeBlur}
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="8"
                                                    value={edgeBlur}
                                                    onChange={(e) => setEdgeBlur(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">
                                                    Umbral de máscara: {maskThreshold}%
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={maskThreshold}
                                                    onChange={(e) => setMaskThreshold(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">
                                                    Recorte de bordes: {edgeTrim}
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="3"
                                                    value={edgeTrim}
                                                    onChange={(e) => setEdgeTrim(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Columna 2 - Configuración de Texto */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-amber-400 mb-3 flex items-center">
                                    <span className="mr-2">🔤</span> Configuración de Texto
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                            Tamaño de Letra: {fontSize}px
                                        </label>
                                        <input
                                            type="range"
                                            min="6"
                                            max="28"
                                            value={fontSize}
                                            onChange={(e) => setFontSize(parseInt(e.target.value))}
                                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                            Densidad: {density}%
                                        </label>
                                        <input
                                            type="range"
                                            min="10"
                                            max="100"
                                            value={density}
                                            onChange={(e) => setDensity(parseInt(e.target.value))}
                                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de Caracteres</label>
                                        <select
                                            value={charType}
                                            onChange={(e) => setCharType(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500"
                                        >
                                            <option value="ramp">Rampa de densidad</option>
                                            <option value="lower">a-z (minúsculas)</option>
                                            <option value="upper">A-Z (mayúsculas)</option>
                                            <option value="alnum">A-Z + 0-9</option>
                                            <option value="symbols">Símbolos</option>
                                            <option value="custom">Personalizado</option>
                                        </select>
                                    </div>

                                    {charType === 'ramp' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Rampa (oscuro→claro)</label>
                                            <input
                                                type="text"
                                                value={rampChars}
                                                onChange={(e) => setRampChars(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:ring-2 focus:ring-amber-500"
                                            />
                                            <p className="text-xs text-slate-500 mt-1">Izquierda = más denso</p>
                                        </div>
                                    )}

                                    {charType === 'custom' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Caracteres Personalizados</label>
                                            <input
                                                type="text"
                                                value={customChars}
                                                onChange={(e) => setCustomChars(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:ring-2 focus:ring-amber-500"
                                                placeholder="Ingresa tus caracteres"
                                            />
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Color de Texto</label>
                                            <input
                                                type="color"
                                                value={textColor}
                                                onChange={(e) => setTextColor(e.target.value)}
                                                className="w-full h-10 bg-slate-900 border border-slate-600 rounded-lg cursor-pointer"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Color de Fondo</label>
                                            <input
                                                type="color"
                                                value={backgroundColor}
                                                onChange={(e) => setBackgroundColor(e.target.value)}
                                                className="w-full h-10 bg-slate-900 border border-slate-600 rounded-lg cursor-pointer"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={bold}
                                                onChange={(e) => setBold(e.target.checked)}
                                                className="w-4 h-4 text-amber-600 bg-slate-900 border-slate-600 rounded focus:ring-amber-500"
                                            />
                                            <span className="text-sm text-slate-300">Negrita</span>
                                        </label>

                                        <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={jitter}
                                                onChange={(e) => setJitter(e.target.checked)}
                                                className="w-4 h-4 text-amber-600 bg-slate-900 border-slate-600 rounded focus:ring-amber-500"
                                            />
                                            <span className="text-sm text-slate-300">Jitter sutil</span>
                                        </label>

                                        {jitter && (
                                            <div className="ml-6 p-2 bg-slate-900/50 rounded-lg border border-slate-600">
                                                <label className="block text-xs text-slate-400 mb-1">
                                                    Cantidad: {jitterAmount}px
                                                </label>
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="5"
                                                    value={jitterAmount}
                                                    onChange={(e) => setJitterAmount(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                        )}

                                        <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={invertColors}
                                                onChange={(e) => setInvertColors(e.target.checked)}
                                                className="w-4 h-4 text-amber-600 bg-slate-900 border-slate-600 rounded focus:ring-amber-500"
                                            />
                                            <span className="text-sm text-slate-300">Invertir colores</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-cyan-400 mb-3 flex items-center">
                                    <span className="mr-2">⚙️</span> Algoritmo y Calidad
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Algoritmo</label>
                                        <select
                                            value={algorithm}
                                            onChange={(e) => setAlgorithm(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
                                        >
                                            <option value="standard">Estándar (tono)</option>
                                            <option value="edge">Bordes (Sobel)</option>
                                            <option value="dual">Doble capa (tono + bordes)</option>
                                            <option value="ascii">ASCII Art Clásico</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Suavizado Previo</label>
                                        <select
                                            value={smoothing}
                                            onChange={(e) => setSmoothing(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
                                        >
                                            <option value="none">Sin suavizado</option>
                                            <option value="light">Ligero</option>
                                            <option value="strong">Fuerte</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                            Resolución: {resolution}px
                                        </label>
                                        <input
                                            type="range"
                                            min="160"
                                            max="800"
                                            step="20"
                                            value={resolution}
                                            onChange={(e) => setResolution(parseInt(e.target.value))}
                                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                            Gamma: {gamma.toFixed(1)}
                                        </label>
                                        <input
                                            type="range"
                                            min="0.5"
                                            max="2.0"
                                            step="0.1"
                                            value={gamma}
                                            onChange={(e) => setGamma(parseFloat(e.target.value))}
                                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Columna 3 - Efectos y Seguridad */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-pink-400 mb-3 flex items-center">
                                    <span className="mr-2">✨</span> Efectos Visuales
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableShadow}
                                            onChange={(e) => setEnableShadow(e.target.checked)}
                                            className="w-4 h-4 text-pink-600 bg-slate-900 border-slate-600 rounded focus:ring-pink-500"
                                        />
                                        <span className="text-sm text-slate-300">Sombra</span>
                                    </label>

                                    {enableShadow && (
                                        <div className="ml-6 space-y-2 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">Color de Sombra</label>
                                                <input
                                                    type="color"
                                                    value={shadowColor}
                                                    onChange={(e) => setShadowColor(e.target.value)}
                                                    className="w-full h-8 bg-slate-900 border border-slate-600 rounded cursor-pointer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">
                                                    Desenfoque: {shadowBlur}px
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="10"
                                                    value={shadowBlur}
                                                    onChange={(e) => setShadowBlur(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableGradient}
                                            onChange={(e) => setEnableGradient(e.target.checked)}
                                            className="w-4 h-4 text-pink-600 bg-slate-900 border-slate-600 rounded focus:ring-pink-500"
                                        />
                                        <span className="text-sm text-slate-300">Gradiente de Color</span>
                                    </label>

                                    {enableGradient && (
                                        <div className="ml-6 space-y-2 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs text-slate-400 mb-1">Color 1</label>
                                                    <input
                                                        type="color"
                                                        value={gradientColor1}
                                                        onChange={(e) => setGradientColor1(e.target.value)}
                                                        className="w-full h-8 bg-slate-900 border border-slate-600 rounded cursor-pointer"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-slate-400 mb-1">Color 2</label>
                                                    <input
                                                        type="color"
                                                        value={gradientColor2}
                                                        onChange={(e) => setGradientColor2(e.target.value)}
                                                        className="w-full h-8 bg-slate-900 border border-slate-600 rounded cursor-pointer"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-slate-400 mb-1">
                                                    Ángulo: {gradientAngle}°
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="360"
                                                    value={gradientAngle}
                                                    onChange={(e) => setGradientAngle(parseInt(e.target.value))}
                                                    className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-red-400 mb-3 flex items-center">
                                    <span className="mr-2">🔐</span> Características de Seguridad
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableWatermark}
                                            onChange={(e) => setEnableWatermark(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">Marca de Agua</span>
                                    </label>

                                    {enableWatermark && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Texto de Marca</label>
                                            <input
                                                type="text"
                                                value={watermarkText}
                                                onChange={(e) => setWatermarkText(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                                placeholder="ORIGINAL"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableMicrotext}
                                            onChange={(e) => setEnableMicrotext(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">Microtexto Integrado</span>
                                    </label>

                                    {enableMicrotext && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Contenido</label>
                                            <input
                                                type="text"
                                                value={microtextContent}
                                                onChange={(e) => setMicrotextContent(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                                placeholder="SECURITY"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableSerialNumber}
                                            onChange={(e) => setEnableSerialNumber(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">Número de Serie</span>
                                    </label>

                                    {enableSerialNumber && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Prefijo</label>
                                            <input
                                                type="text"
                                                value={serialPrefix}
                                                onChange={(e) => setSerialPrefix(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                                placeholder="GH"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-indigo-400 mb-3 flex items-center">
                                    <span className="mr-2">⚡</span> Optimización
                                </h4>

                                <div className="space-y-2">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableAntiAlias}
                                            onChange={(e) => setEnableAntiAlias(e.target.checked)}
                                            className="w-4 h-4 text-indigo-600 bg-slate-900 border-slate-600 rounded focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-slate-300">Anti-aliasing</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableDithering}
                                            onChange={(e) => setEnableDithering(e.target.checked)}
                                            className="w-4 h-4 text-indigo-600 bg-slate-900 border-slate-600 rounded focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-slate-300">Dithering</span>
                                    </label>

                                    {enableDithering && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Tipo de Dithering</label>
                                            <select
                                                value={ditheringType}
                                                onChange={(e) => setDitheringType(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                            >
                                                <option value="floyd-steinberg">Floyd-Steinberg</option>
                                                <option value="atkinson">Atkinson</option>
                                                <option value="ordered">Ordered (Bayer)</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Información Técnica */}
                            <div className="bg-gradient-to-br from-gray-900/30 to-slate-900/30 rounded-lg p-4 border border-gray-700/50">
                                <h4 className="text-sm font-bold text-gray-300 mb-2">ℹ️ Información</h4>
                                <div className="space-y-1 text-xs text-slate-400">
                                    <p>• <strong>Tecnología:</strong> LetterScreen ASCII Art</p>
                                    <p>• <strong>Aplicaciones:</strong> Documentos de seguridad</p>
                                    <p>• <strong>Anti-falsificación:</strong> Imposible de copiar</p>
                                    <p>• <strong>Verificación:</strong> Visual directa</p>
                                    <p>• <strong>Nivel:</strong> Profesional / Clase B+</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-between items-center">
                    <div className="text-xs text-slate-500">
                        <p>👻 Tecnología LetterScreen Profesional</p>
                        <p>Conversión avanzada de imagen a patrón de texto anti-falsificación</p>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleGenerate}
                            disabled={!selectedFile}
                            className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all shadow-lg ${selectedFile
                                ? 'bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-slate-900 shadow-slate-500/50'
                                : 'bg-slate-700 cursor-not-allowed opacity-50'
                                }`}
                        >
                            👻 Generar Imagen Fantasma
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GhostImageModal;
